import { useEffect, useRef } from 'react';
import { User, Layers, MessageSquare } from 'lucide-react';

const principles = [
  {
    icon: User,
    title: 'Person first, product last.',
    desc: 'We start with you, not with a passport program.',
    span: 'md:col-span-4',
  },
  {
    icon: Layers,
    title: 'All of it, not one piece.',
    desc: 'We work across tax, citizenship, capital, and succession.',
    span: 'md:col-span-4',
  },
  {
    icon: MessageSquare,
    title: 'Counsel over sales.',
    desc: 'We only recommend changes worth making. We do not manufacture work to extend the engagement.',
    span: 'md:col-span-4',
  },
];

export default function Principles() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const revealElements = sectionRef.current?.querySelectorAll('.reveal');
    revealElements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="principles"
      ref={sectionRef}
      data-testid="principles-section"
      className="relative py-24 md:py-32 bg-[#f7f6f3]"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-16 reveal">
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#1a7a7a] mb-4 font-medium"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            How We Work
          </p>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1a1a1a]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            The Three Principles
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {principles.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                data-testid={`principle-card-${idx}`}
                className={`${item.span} reveal stagger-${idx + 1} group relative overflow-hidden rounded-2xl bg-white border border-gray-200 p-8 md:p-10 hover:border-[#1a7a7a]/40 hover:shadow-lg transition-all duration-500`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#1a7a7a]/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-[#1a7a7a]/10 border border-[#1a7a7a]/15 flex items-center justify-center mb-6 group-hover:bg-[#1a7a7a]/15 transition-colors duration-300">
                    <Icon className="text-[#1a7a7a]" size={28} />
                  </div>
                  <h3
                    className="text-xl md:text-2xl font-medium text-[#1a1a1a] mb-4"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-base text-gray-500 leading-relaxed"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {item.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
